<script setup lang="ts">
import { computed } from "vue";
import { storeToRefs } from "pinia";
import { n } from "components/jiffy/vue-cart/helpers/currency";
import { useDtfStore } from "./store";

const dtfStore = useDtfStore();
const { artworks, summary, isPricingLoading } = storeToRefs(dtfStore);

const orderTotal = computed(() => Number(summary.value.orderTotal));
const bulkDiscount = computed(() => Number(summary.value.bulkDiscount));
const orderSubtotal = computed(() => Number(summary.value.orderSubtotal));
const uploadedCount = computed(
  () => artworks.value.filter(({ uploadError }) => !uploadError).length
);
const enhancements = computed(() => Object.values(summary.value.enhancements || []));
const artworksReady = computed(() =>
  artworks.value.every(artwork => !artwork.isLoading && artwork.enhancementCompleted)
);
const enableAbAiMonetization = computed(() => dtfStore.enableAbAiMonetization);

const isBogoApplicable = computed(() => dtfStore.isBogoApplicable);
const getBogoAdjustedPrice = computed(() => dtfStore.getBogoAdjustedPrice);

const bogoInfo = computed(() => {
  if (!isBogoApplicable.value) {
    return { freeQty: 0, discount: 0 };
  }

  let freeQty = 0;
  let discountAmount = 0;

  artworks.value.forEach(artwork => {
    const artworkId = String(artwork.id ?? "");
    const sizesAndQuantities = artwork.sizesAndQuantities ?? [];

    sizesAndQuantities.forEach(item => {
      const sizeKey = `${item.width}x${item.height}`;
      const bogoPrice = getBogoAdjustedPrice.value(artworkId, item.id, sizeKey);

      if (bogoPrice && bogoPrice.freeQty > 0) {
        freeQty += bogoPrice.freeQty;
        const itemOriginalTotal = Number(item.unitPrice || 0) * item.quantity;
        discountAmount += itemOriginalTotal - bogoPrice.discountedTotalPrice;
      }
    });
  });

  return { freeQty, discount: discountAmount };
});
</script>

<template>
  <div class="summary">
    <h3 class="summary__title">Summary</h3>
    <div class="summary__wrapper">
      <div
        v-if="enableAbAiMonetization && (isPricingLoading || !artworksReady)"
        class="summary__skeleton"
      >
        <div class="summary__skeleton-block"></div>
        <div class="summary__skeleton-block"></div>
        <div class="summary__skeleton-block"></div>
        <div class="summary__skeleton-block"></div>
        <div class="summary__skeleton-block summary__skeleton-block--last"></div>
      </div>
      <template v-else>
        <div class="summary__row">
          <div class="summary__label">Designs uploaded:</div>
          <div class="summary__value">{{ uploadedCount }}</div>
        </div>
        <div v-if="orderTotal" class="summary__row">
          <div class="summary__label">Order total:</div>
          <div class="summary__value">{{ n(orderTotal, "currency") }}</div>
        </div>
        <div
          v-for="enhancement in enhancements"
          :key="enhancement.serviceName"
          class="summary__row"
        >
          <template v-if="Number(enhancement.totalPrice) > 0">
            <div class="summary__label">
              {{ enhancement.quantity }} {{ enhancement.serviceName }}:
            </div>
            <div class="summary__value">{{ n(enhancement.totalPrice, "currency") }}</div>
          </template>
        </div>
        <div v-if="bulkDiscount" class="summary__row summary__row--bulk">
          <div class="summary__label">Bulk discount:</div>
          <div class="summary__value">-{{ n(bulkDiscount, "currency") }}</div>
        </div>
        <div v-if="bogoInfo.freeQty > 0" class="summary__row summary__row--bogo">
          <div class="summary__label">
            {{ bogoInfo.freeQty }} FREE {{ bogoInfo.freeQty === 1 ? "Transfer" : "Transfers" }}:
          </div>
          <div class="summary__value">-{{ n(bogoInfo.discount, "currency") }}</div>
        </div>
        <div
          v-if="orderSubtotal || bogoInfo.freeQty > 0"
          class="summary__row summary__row--subtotal"
        >
          <div class="summary__label">Subtotal</div>
          <div class="summary__value">
            {{
              n(
                bogoInfo.freeQty > 0 ? orderSubtotal - bogoInfo.discount : orderSubtotal,
                "currency"
              )
            }}
          </div>
        </div>
        <div v-if="bogoInfo.discount > 0" class="summary__row summary__row--savings">
          <div class="summary__label"></div>
          <div class="summary__value summary__savings">
            You save {{ n(bogoInfo.discount + bulkDiscount, "currency") }}!
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

.summary {
  margin-bottom: 32px;

  &__title {
    padding: 0;
    margin-bottom: 12px;
    font-size: 22px;
    font-weight: 700;
  }

  &__wrapper {
    border-radius: 4px;
    border: 1px solid $gray-border;
    background: $white;
    padding: 16px;
  }

  &__skeleton {
    display: flex;
    flex-direction: column;
    gap: 11px;
    width: 280px;
  }

  &__skeleton-block {
    @include vlc-animation;

    position: relative;
    width: 100%;
    height: 18px;
    border-radius: 4px;
    background-color: $section-grey-border;

    &--last {
      margin-top: 30px;
    }
  }

  &__row {
    text-align: right;
    font-size: 14px;
    font-weight: 700;
    display: flex;
    align-items: center;

    & + & {
      margin-top: 8px;
    }

    &--bulk {
      color: $price-product-card;
    }

    &--bogo {
      color: $feedback-success-darker;
    }

    &--subtotal {
      padding-top: 24px;
      font-size: 20px;
    }
  }

  &__savings {
    color: $feedback-success-darker;
    font-size: 14px;
    font-weight: 600;
    text-align: right;
  }

  &__label,
  &__value {
    flex: 1 0 50%;
  }
}
</style>
